import { generateHTML } from "@tiptap/core";
import { getBuilderTiptapExtensions } from "@/react/components/shared/BuilderUI/Editor/fields/RichTextField/TipTapEditor";
import type { TipTapContent } from "./textTypes";

/**
 * Helper functions for creating and managing rich text content in Builder UI blocks
 */

/**
 * Converts plain text to TipTap JSON format
 */
export const createTipTapFromText = (text: string): TipTapContent => {
  if (!text || text.trim() === "") {
    return {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [],
        },
      ],
    };
  }

  // Split text by line breaks and create paragraph nodes
  const paragraphs = text.split("\n").map(line => ({
    type: "paragraph",
    content:
      line.trim() === ""
        ? []
        : [
            {
              type: "text",
              text: line,
            },
          ],
  }));

  return {
    type: "doc",
    content: paragraphs,
  };
};

/**
 * Helper to create a rich text prop for block default data
 */
export const createRichTextProp = (defaultText: string) => ({
  prop: "other" as const,
  type: "rich-text" as const,
  value: createTipTapFromText(defaultText),
});

/**
 * Checks if content is in rich text format (TipTap JSON)
 */
export const isRichTextContent = (content: unknown): content is TipTapContent =>
  typeof content === "object" &&
  content !== null &&
  "type" in content &&
  "content" in content &&
  content.type === "doc" &&
  Array.isArray(content.content);

/**
 * Converts TipTap content to HTML string for rendering in non-editor contexts
 */
export const convertTipTapToHTML = (content: TipTapContent): string => {
  if (!content?.content) return "";
  return generateHTML(content, getBuilderTiptapExtensions());
};

export const isPlainText = (text: unknown): text is string =>
  typeof text === "string";
